
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { safeReload } from '@/utils/navigation';

interface UpdateState {
  isUpdateAvailable: boolean;
  isUpdating: boolean;
  updateError: string | null;
  lastUpdateCheck: Date | null;
}

export const useUpdateManager = () => {
  const [updateState, setUpdateState] = useState<UpdateState>({
    isUpdateAvailable: false,
    isUpdating: false,
    updateError: null,
    lastUpdateCheck: null
  });

  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);

  // Check for updates without forcing immediate reload
  const checkForUpdates = useCallback(async () => {
    if (!('serviceWorker' in navigator)) {
      return false;
    }

    try {
      const reg = await navigator.serviceWorker.ready;
      setRegistration(reg);
      
      // Check if there's a waiting service worker
      if (reg.waiting) {
        setUpdateState(prev => ({ 
          ...prev, 
          isUpdateAvailable: true,
          lastUpdateCheck: new Date()
        }));
        return true;
      }

      // Check for updates
      await reg.update();
      
      setUpdateState(prev => ({ 
        ...prev, 
        lastUpdateCheck: new Date()
      }));
      
      return false;
    } catch (error) {
      console.error('Error checking for updates:', error);
      setUpdateState(prev => ({ 
        ...prev, 
        updateError: 'Failed to check for updates',
        lastUpdateCheck: new Date()
      }));
      return false;
    }
  }, []);

  // Apply update with user consent
  const applyUpdate = useCallback(async () => {
    if (!registration || !registration.waiting) {
      return false;
    }

    setUpdateState(prev => ({ ...prev, isUpdating: true, updateError: null }));

    try {
      // Post message to waiting service worker to skip waiting
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      
      // Set up listener for controller change
      const handleControllerChange = () => {
        setUpdateState(prev => ({ ...prev, isUpdating: false }));
        toast.success('App updated successfully!');
        
        // Gentle reload after a short delay
        setTimeout(() => {
          safeReload('update-manager-apply');
        }, 1000);
      };

      navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange, { once: true });
      
      return true;
    } catch (error) {
      console.error('Error applying update:', error);
      setUpdateState(prev => ({ 
        ...prev, 
        isUpdating: false,
        updateError: 'Failed to apply update'
      }));
      return false;
    }
  }, [registration]);

  // Dismiss update notification
  const dismissUpdate = useCallback(() => {
    setUpdateState(prev => ({ 
      ...prev, 
      isUpdateAvailable: false 
    }));
  }, []);

  // Set up service worker listeners
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handleUpdateFound = () => {
      console.log('New service worker found');
    };

    const handleStateChange = (event: Event) => {
      const worker = event.target as ServiceWorker;
      if (worker.state === 'installed' && navigator.serviceWorker.controller) {
        setUpdateState(prev => ({ 
          ...prev, 
          isUpdateAvailable: true 
        }));
      }
    };

    navigator.serviceWorker.ready.then(reg => {
      setRegistration(reg);
      
      reg.addEventListener('updatefound', handleUpdateFound);
      
      if (reg.installing) {
        reg.installing.addEventListener('statechange', handleStateChange);
      }
      
      // Check for waiting worker on load
      if (reg.waiting) {
        setUpdateState(prev => ({ 
          ...prev, 
          isUpdateAvailable: true 
        }));
      }
    });

    return () => {
      if (registration) {
        registration.removeEventListener('updatefound', handleUpdateFound);
      }
    };
  }, [registration]);

  // Auto-check for updates periodically (less aggressive)
  useEffect(() => {
    // Only check for updates every 10 minutes in production
const isDev = import.meta.env.DEV;
const interval = isDev ? 30000 : 600000; // 30s in dev, 10min in prod
    
    const timer = setInterval(checkForUpdates, interval);
    
    // Initial check after 5 seconds
    const initialTimer = setTimeout(checkForUpdates, 5000);
    
    return () => {
      clearInterval(timer);
      clearTimeout(initialTimer);
    };
  }, [checkForUpdates]);

  return {
    ...updateState,
    checkForUpdates,
    applyUpdate,
    dismissUpdate
  };
};
